import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import { AddonKey, AddonService, AddonStates } from '../../services/addon.service';

// Fired on window after the platform switches an add-on, so the buttons re-read the switches.
export const ADDONS_CHANGED_EVENT = 'oms:addons-changed';

const ALL_ON: AddonStates = { help: true, feedback: true, xero: true };

/**
 * Whether an add-on (help, feedback) shows for the current user: switched on for every institution AND, when an
 * institution is selected, in that institution's modules. A failed read keeps it on, so a network blip never
 * hides help.
 */
export function useAddon(key: AddonKey): boolean {
    const { isAuthenticated } = useAuth();
    const { enabledModules } = useDashboardAccess();
    const [global, setGlobal] = useState<AddonStates>(ALL_ON);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        const bump = () => setVersion(v => v + 1);
        window.addEventListener(ADDONS_CHANGED_EVENT, bump);
        return () => window.removeEventListener(ADDONS_CHANGED_EVENT, bump);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;
        let alive = true;
        AddonService.getGlobal()
            .then(states => { if (alive) setGlobal({ ...ALL_ON, ...states }); })
            .catch(() => { if (alive) setGlobal(ALL_ON); });
        return () => { alive = false; };
    }, [isAuthenticated, version]);

    // With no institution selected enabledModules holds every key, so only the platform switch counts.
    return global[key] && enabledModules.has(key);
}
